import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, AlertTriangle, Clock, CheckCircle, XCircle,
  MessageSquare, DollarSign, Package, ChevronDown, ChevronUp
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import Logo from '@/components/Logo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const REASON_LABELS = {
  not_as_described: 'Not as Described',
  not_received: 'Not Received',
  quality_issue: 'Quality Issue',
  other: 'Other'
};

const STATUS_STYLES = {
  open: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', icon: Clock },
  under_review: { bg: 'bg-[#00F0FF]/20', text: 'text-[#00F0FF]', icon: Clock },
  resolved: { bg: 'bg-green-500/20', text: 'text-green-400', icon: CheckCircle },
  rejected: { bg: 'bg-red-500/20', text: 'text-red-400', icon: XCircle }
};

function MyDisputes() {
  const navigate = useNavigate();
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/');
      return;
    }
    loadDisputes();
  }, []);

  const loadDisputes = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/disputes/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDisputes(res.data.disputes || []);
    } catch (error) {
      toast.error('Failed to load disputes');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-[#00F0FF]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <header className="border-b border-white/10 backdrop-blur-xl bg-black/40 sticky top-0 z-50">
        <div className="container mx-auto px-6 md:px-12 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => navigate('/my-purchases')}
              className="text-[#00F0FF] hover:text-[#00C2CC]"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Purchases
            </Button>
            <Logo />
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 md:px-12 py-12">
        <div className="flex items-center gap-3 mb-8">
          <AlertTriangle className="w-8 h-8 text-[#00F0FF]" />
          <h1 className="text-3xl font-bold">My Disputes & Refunds</h1>
        </div>

        {disputes.length === 0 ? (
          <div className="text-center py-16 bg-neutral-900/50 border border-white/10 rounded-2xl">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">No disputes</h2>
            <p className="text-neutral-400">You haven't filed any disputes or refund requests.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {disputes.map((dispute) => {
              const status = STATUS_STYLES[dispute.status] || STATUS_STYLES.open;
              const StatusIcon = status.icon;
              const isExpanded = expandedId === dispute.dispute_id;

              return (
                <div
                  key={dispute.dispute_id}
                  className="bg-neutral-900/50 border border-white/10 rounded-2xl overflow-hidden"
                >
                  <div
                    className="p-6 cursor-pointer hover:bg-white/5 transition-colors"
                    onClick={() => setExpandedId(isExpanded ? null : dispute.dispute_id)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-xl bg-neutral-800 flex items-center justify-center">
                          <Package className="w-6 h-6 text-neutral-500" />
                        </div>
                        <div>
                          <h3 className="font-semibold mb-1">{dispute.product_name}</h3>
                          <p className="text-sm text-neutral-400">Order: {dispute.order_id}</p>
                          <p className="text-sm text-neutral-400">
                            Filed: {formatDate(dispute.created_at)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className={`inline-flex items-center gap-1 px-3 py-1 ${status.bg} ${status.text} text-sm rounded-full`}>
                          <StatusIcon className="w-4 h-4" />
                          {dispute.status.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
                        </span>
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5 text-neutral-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-neutral-400" />
                        )}
                      </div>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="px-6 pb-6 border-t border-white/10 pt-4">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <h4 className="text-sm font-medium text-neutral-400 mb-2">Reason</h4>
                          <p className="mb-4">{REASON_LABELS[dispute.reason] || dispute.reason}</p>
                          
                          <h4 className="text-sm font-medium text-neutral-400 mb-2">Description</h4>
                          <p className="text-neutral-300">{dispute.description}</p>
                        </div>
                        <div>
                          <h4 className="text-sm font-medium text-neutral-400 mb-2">Order Amount</h4>
                          <p className="text-xl font-bold mb-4">${dispute.order_amount?.toFixed(2)}</p>
                          
                          {dispute.resolution && (
                            <>
                              <h4 className="text-sm font-medium text-neutral-400 mb-2">Resolution</h4>
                              <p className="capitalize mb-2">
                                {dispute.resolution.replace('_', ' ')}
                              </p>
                              {dispute.refund_amount && (
                                <p className="text-[#00F0FF] font-bold">
                                  Refunded: ${dispute.refund_amount.toFixed(2)}
                                </p>
                              )}
                            </>
                          )}
                          
                          {dispute.seller_response && (
                            <div className="mt-4 p-4 bg-neutral-800/50 rounded-xl">
                              <h4 className="text-sm font-medium text-neutral-400 mb-2">Seller Response</h4>
                              <p className="text-neutral-300">{dispute.seller_response}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}

export default MyDisputes;
